import { Eye, EyeOff } from "lucide-react";
import {
  CHECKBOX_TYPES,
  CURRENCY,
  NUMBER,
  DATE_TYPES,
  DROPDOWN_TYPES,
  EIN,
  PERCENT,
  PHONE_NUMBER,
  RADIO,
  SSN,
  HYPERLINK,
  MULTILINE_TEXTFIELD,
  DROPDOWN_WITH_TEXTFIELD,
  CONTAINER,
  DUMMY,
  ONE_TO_MANY_RADIO,
  DROPDOWN_TYPES_MONTHS,
  HORIZONTAL_LINE,
} from "../../../utils/InputFieldConfig";
import {
  checkBoxReadableValues,
  getDropDownLabel,
  maskSSN,
  getDropDownText,
  RadioMultiSupport,
  getMonthsLabels,
  formatEIN,
  formatSSN,
} from "../label-utils/label-utils";
import { ContainerInput } from "../container-input/container-input";
import { displayOnlyYear, humanReadableDate } from "../../../utils/dateHelper";
import { roundUpCurrency } from "../../../utils/utils";
import { HorizontalLineField } from "../horizontal-line/horizontal-line-field";

export const LabelFields = (
  fieldValue,
  item,
  error,
  onFieldChange,
  classes, // unused; kept for API parity
  readOnly,
  fieldValues,
  componentContext,
  handleFieldLevelHandler,
  currentPiiInfoFields,
  isTooltip = false
) => {
  const handleClick = (event) => {
    event.preventDefault();
    if (!fieldValue) return;
    const url = String(fieldValue).startsWith("http")
      ? String(fieldValue)
      : `https://${fieldValue}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  switch (item?.type?.toLowerCase()) {
    case DROPDOWN_TYPES:
      return getDropDownLabel(item.values || item.options, fieldValue);

    case DATE_TYPES:
      return item.onlyYears
        ? displayOnlyYear(fieldValue)
        : humanReadableDate(fieldValue);

    case ONE_TO_MANY_RADIO:
      return RadioMultiSupport(item.values, fieldValue);

    case CHECKBOX_TYPES:
      return checkBoxReadableValues(fieldValue);

    case RADIO:
      return checkBoxReadableValues(fieldValue);

    case SSN: {
      const masked = currentPiiInfoFields?.ssn
        ? maskSSN(fieldValue)
        : formatSSN(fieldValue);
      return (
        <span className="inline-flex items-center gap-2">
          <span className={masked === "--" ? "" : "font-mono tracking-wider"}>{masked}</span>
          {!readOnly && (
            <button
              type="button"
              onClick={() =>
                handleFieldLevelHandler?.(item, currentPiiInfoFields?.ssn)
              }
              className="p-1 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300"
              aria-label={currentPiiInfoFields?.ssn ? "Hide SSN" : "Show SSN"}
            >
              {currentPiiInfoFields?.ssn ? (
                <EyeOff className="h-4 w-4 text-gray-600" />
              ) : (
                <Eye className="h-4 w-4 text-gray-600" />
              )}
            </button>
          )}
        </span>
      );
    }

    case HYPERLINK:
      if (!fieldValue) return "--";
      return (
        <a
          href={`${fieldValue}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClick}
          className="text-gray-900 underline underline-offset-2 hover:text-gray-700"
        >
          {fieldValue}
        </a>
      );

    case CURRENCY: {
      if (!fieldValue && fieldValue !== 0) return "--";
      const digits = item?.roundOffToTwoDecimal ? 2 : 0;
      return roundUpCurrency(fieldValue, digits) || "--";
    }

    case EIN:
      return fieldValue ? formatEIN(fieldValue) : "--";

    case NUMBER:
      return fieldValue || fieldValue === 0
        ? Number(fieldValue)?.toLocaleString()
        : "--";

    case PERCENT:
      return fieldValue || fieldValue === 0
        ? `${Number(fieldValue)?.toFixed(item?.percentAcceptedLimit || 2)}%`
        : "--";

    case PHONE_NUMBER:
      return fieldValue || "--";

    case MULTILINE_TEXTFIELD:
      return (
        <span className="block max-w-full whitespace-pre-wrap break-words leading-relaxed">
          {fieldValue || "--"}
        </span>
      );

    case DROPDOWN_WITH_TEXTFIELD:
      return getDropDownText(fieldValue, isTooltip);

    case DROPDOWN_TYPES_MONTHS:
      return getMonthsLabels(fieldValue);

    case CONTAINER:
      return (
        <ContainerInput
          readOnly={readOnly}
          fields={item.fields}
          componentContext={componentContext}
          onFieldChange={onFieldChange}
          fieldValues={fieldValues?.[item.uniqueField]}
        />
      );

    case HORIZONTAL_LINE:
      return (
        <HorizontalLineField />
      );

    case DUMMY:
      return null;

    default:
      return fieldValue || fieldValue === 0 ? fieldValue : "--";
  }
};
